import { useMemo, useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from '@/components/ui/sonner';
import { useRolePermissions, useUpdateRolePermissions } from '@/hooks/use-role-permissions';
import type { RoleName } from '@/stores/permissions.store';

const ROLE_LABELS: Record<RoleName, string> = {
	administrator: 'Administrator',
	agent: 'Agent',
	ai: 'AI',
	light_agent: 'Light Agent',
	client_restricted: 'Client Restricted User',
	sentinel: 'Sentinel',
	readonly_admin: 'Read-only Admin'
};

const ACTIONS = ['view', 'edit'] as const;

/** Mirrors READONLY_ADMIN_EDITABLE_SUBJECTS in kim-ai-backend: the simulator and KB test. */
const READONLY_ADMIN_EDITABLE_SUBJECTS = new Set(['playground', 'kb-test']);

/**
 * Catalog keys ship as `<subject>.<action>`. Split into subjects so the grid
 * shows one row per subject with a column per action.
 */
function deriveSubjects(catalog: string[]): string[] {
	const subjects = new Set<string>();
	for (const key of catalog) {
		const idx = key.lastIndexOf('.');
		if (idx === -1) continue;
		const action = key.slice(idx + 1);
		if (action !== 'view' && action !== 'edit') continue;
		subjects.add(key.slice(0, idx));
	}
	return [...subjects].sort();
}

interface RoleEditorProps {
	role: RoleName;
	catalog: string[];
	initialPermissions: string[];
}

function RoleEditor({ role, catalog, initialPermissions }: RoleEditorProps) {
	const subjects = useMemo(() => deriveSubjects(catalog), [catalog]);
	const catalogSet = useMemo(() => new Set(catalog), [catalog]);
	const [selected, setSelected] = useState<Set<string>>(() => new Set(initialPermissions));
	const update = useUpdateRolePermissions();

	const isDirty = useMemo(() => {
		const initial = new Set(initialPermissions);
		if (selected.size !== initial.size) return true;
		for (const k of selected) if (!initial.has(k)) return true;
		return false;
	}, [selected, initialPermissions]);

	const toggle = (key: string) => {
		if (!catalogSet.has(key)) return;
		setSelected((prev) => {
			const next = new Set(prev);
			if (next.has(key)) next.delete(key);
			else next.add(key);
			return next;
		});
	};

	const handleSave = async () => {
		try {
			await update.mutateAsync({ role, permissions: [...selected] });
			toast.success(`${ROLE_LABELS[role]} permissions saved`);
		} catch (err) {
			const message = err instanceof Error ? err.message : 'Failed to save';
			toast.error(message);
		}
	};

	return (
		<div className='flex flex-col gap-4'>
			<div className='flex items-center justify-between'>
				<p className='text-sm text-muted-foreground'>
					{selected.size} of {catalog.length} permissions granted
				</p>
				<Button onClick={handleSave} disabled={!isDirty || update.isPending}>
					{update.isPending ? <Loader2 className='size-4 animate-spin' /> : <Save className='size-4' />}
					Save changes
				</Button>
			</div>
			<Table>
				<TableHeader>
					<TableRow>
						<TableHead>Subject</TableHead>
						{ACTIONS.map((a) => (
							<TableHead key={a} className='w-24 text-center capitalize'>
								{a}
							</TableHead>
						))}
					</TableRow>
				</TableHeader>
				<TableBody>
					{subjects.map((subject) => (
						<TableRow key={subject}>
							<TableCell className='font-mono'>{subject}</TableCell>
							{ACTIONS.map((action) => {
								const key = `${subject}.${action}`;
								const available = catalogSet.has(key);
								return (
									<TableCell key={action} className='text-center'>
										{available ? (
											<Checkbox
												checked={selected.has(key)}
												onCheckedChange={() => toggle(key)}
												// The read-only role may edit only the test surfaces; the backend rejects any other edit key.
												disabled={role === 'readonly_admin' && action === 'edit' && !READONLY_ADMIN_EDITABLE_SUBJECTS.has(subject)}
												aria-label={`${subject} ${action}`}
											/>
										) : (
											<span className='text-muted-foreground'>—</span>
										)}
									</TableCell>
								);
							})}
						</TableRow>
					))}
				</TableBody>
			</Table>
		</div>
	);
}

export function RolePermissionsGrid() {
	const query = useRolePermissions();

	if (query.isLoading) {
		return (
			<div className='flex items-center justify-center py-16'>
				<Loader2 className='size-6 animate-spin' />
			</div>
		);
	}

	if (query.isError || !query.data) {
		return (
			<p className='text-sm text-destructive'>
				Failed to load role permissions{query.error instanceof Error ? `: ${query.error.message}` : ''}.
			</p>
		);
	}

	const { roles, catalog, items } = query.data;
	const initialFor = (role: RoleName) =>
		items.find((i) => i.role === role)?.permissions ?? [];

	const defaultRole: RoleName = roles[0] ?? 'administrator';

	return (
		<Tabs defaultValue={defaultRole} className='w-full'>
			<TabsList>
				{roles.map((r) => (
					<TabsTrigger key={r} value={r}>
						{ROLE_LABELS[r] ?? r}
					</TabsTrigger>
				))}
			</TabsList>
			{roles.map((r) => (
				<TabsContent key={r} value={r}>
					<RoleEditor
						key={`${r}-${items.find((i) => i.role === r)?.updatedAt ?? 'init'}`}
						role={r}
						catalog={catalog}
						initialPermissions={initialFor(r)}
					/>
				</TabsContent>
			))}
		</Tabs>
	);
}
